import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { AreaChart, Area, CartesianGrid, XAxis, YAxis } from "recharts";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

import {
  getLlmCostByOrganization,
  getLlmCostSummary,
  getLlmCostTimeseries,
  getLlmTopCalls,
  formatTokens,
  formatUsd,
  type CostBucket,
  type LlmCostSummary,
  type OrganizationCostRow,
  type TimeseriesPoint,
  type TopCallRow,
} from "@/services/llmCostsApi";

const WINDOWS = [7, 30, 90, 365];

const chartConfig: ChartConfig = {
  estimated_cost_usd: { label: "Coût estimé (USD)", color: "hsl(var(--chart-1))" },
};

/** Lien vers la source qui fait foi sur la facture. */
const GATEWAY_UI = "https://llm.internal.diametral.com";

function Kpi({
  title,
  value,
  hint,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  tone?: "warning";
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-semibold ${
            tone === "warning" ? "text-amber-600 dark:text-amber-500" : ""
          }`}
        >
          {value}
        </div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function BucketTable({
  rows,
  label,
  emptyLabel,
}: {
  rows: CostBucket[];
  label: string;
  emptyLabel: string;
}) {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{label}</TableHead>
          <TableHead className="text-right">Appels</TableHead>
          <TableHead className="text-right">Entrée</TableHead>
          <TableHead className="text-right">Sortie</TableHead>
          <TableHead className="text-right">Coût estimé</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key}>
            <TableCell className="font-mono text-xs">{r.key}</TableCell>
            <TableCell className="text-right">{r.calls}</TableCell>
            <TableCell className="text-right">{formatTokens(r.prompt_tokens)}</TableCell>
            <TableCell className="text-right">
              {formatTokens(r.completion_tokens)}
            </TableCell>
            <TableCell className="text-right font-medium">
              {r.unpriced_calls > 0 && r.estimated_cost_usd === 0 ? (
                // Tout le bucket est non tarifé : afficher « $0 » ici mentirait.
                <span className="text-amber-600 dark:text-amber-500">non tarifé</span>
              ) : (
                <>
                  {formatUsd(r.estimated_cost_usd)}
                  {r.unpriced_calls > 0 && (
                    <span
                      className="ml-1 text-xs font-normal text-amber-600 dark:text-amber-500"
                      title={`${r.unpriced_calls} appel(s) sans tarif connu — montant sous-estimé`}
                    >
                      +{r.unpriced_calls}?
                    </span>
                  )}
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function LlmCostsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<LlmCostSummary | null>(null);
  const [orgs, setOrgs] = useState<OrganizationCostRow[]>([]);
  const [points, setPoints] = useState<TimeseriesPoint[]>([]);
  const [topCalls, setTopCalls] = useState<TopCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, o, t, c] = await Promise.all([
        getLlmCostSummary(navigate, days),
        getLlmCostByOrganization(navigate, days),
        getLlmCostTimeseries(navigate, days),
        getLlmTopCalls(navigate, days),
      ]);
      setSummary(s);
      setOrgs(o.results);
      setPoints(t.points);
      setTopCalls(c.results);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = summary?.totals;
  const unpriced = totals?.unpriced_calls ?? 0;

  return (
    <>
      <AppSidebar />
      <SidebarInset>
      <header className="flex h-16 shrink-0 items-center gap-2">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Coûts LLM</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      <main className="flex-1 space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">Coûts LLM</h1>
            <p className="text-sm text-muted-foreground">
              Dépense estimée des appels de complétion, par usage, par modèle et
              par organisation.
            </p>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d} derniers jours
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* La provenance du chiffre, affichée à côté du chiffre — pas enfouie
            dans une doc. Un opérateur qui lit "$42" doit savoir en une ligne que
            ce n'est pas la facture. */}
        <Card className="border-muted-foreground/20 bg-muted/30">
          <CardContent className="flex flex-wrap items-start gap-3 py-4 text-sm">
            <div className="flex-1 min-w-[280px]">
              <p>
                <strong>Ces montants sont des estimations</strong> — tokens
                mesurés × grille tarifaire locale. La passerelle LiteLLM détient
                la grille du fournisseur et <strong>fait foi sur la facture</strong>.
              </p>
              <p className="mt-1 text-muted-foreground">
                Ce que cet écran apporte, et que la passerelle ne peut pas donner :
                la répartition <strong>par organisation</strong> (back/ l'appelle
                avec une clé virtuelle unique pour tous les tenants). Un écart qui
                se creuse entre les deux = grille locale à corriger.
              </p>
            </div>
            <a
              href={GATEWAY_UI}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium underline underline-offset-4"
            >
              Passerelle LiteLLM
              <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-destructive">
            <CardContent className="py-4 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {unpriced > 0 && (
          <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
            <CardContent className="flex items-start gap-3 py-4 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <strong>
                  {unpriced} appel{unpriced > 1 ? "s" : ""} sans tarif connu
                </strong>{" "}
                sur la période : leur modèle est absent de la grille locale, donc
                ils comptent pour zéro. Le coût affiché ci-dessous est{" "}
                <strong>sous-estimé</strong> — une courbe plate n'est pas une bonne
                nouvelle tant que ce compteur n'est pas revenu à zéro. Correctif :
                ajouter le modèle à <code>LLM_PRICE_TABLE</code>.
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Kpi
            title="Coût estimé"
            value={totals ? formatUsd(totals.estimated_cost_usd) : "—"}
            hint={`sur ${days} jours`}
          />
          <Kpi title="Appels" value={totals ? String(totals.calls) : "—"} />
          <Kpi
            title="Tokens entrée"
            value={totals ? formatTokens(totals.prompt_tokens) : "—"}
          />
          <Kpi
            title="Tokens sortie"
            value={totals ? formatTokens(totals.completion_tokens) : "—"}
            hint="facturés plus cher que l'entrée"
          />
          <Kpi
            title="Échecs"
            value={totals ? String(totals.errors) : "—"}
            hint="tokens inconnus, coût non compté"
            tone={totals && totals.errors > 0 ? "warning" : undefined}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Coût estimé par jour</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Chargement...
              </p>
            ) : points.length === 0 ? (
              // Pas de zéros inventés : une journée sans appel est absente, ce qui
              // permet de distinguer "rien dépensé" d'une panne de la feature.
              <p className="py-8 text-center text-sm text-muted-foreground">
                Aucun appel sur la période.
              </p>
            ) : (
              <ChartContainer config={chartConfig} className="max-h-[280px] w-full">
                <AreaChart data={points}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => {
                      const parts = String(v).split("-");
                      return parts.length === 3
                        ? `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}`
                        : v;
                    }}
                  />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="estimated_cost_usd"
                    stroke="var(--color-estimated_cost_usd)"
                    fill="var(--color-estimated_cost_usd)"
                    fillOpacity={0.4}
                  />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Par usage</CardTitle>
            </CardHeader>
            <CardContent>
              <BucketTable
                rows={summary?.by_operation ?? []}
                label="Usage"
                emptyLabel="Aucun appel sur la période."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Par modèle</CardTitle>
            </CardHeader>
            <CardContent>
              <BucketTable
                rows={summary?.by_model ?? []}
                label="Modèle facturé"
                emptyLabel="Aucun appel sur la période."
              />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Par organisation</CardTitle>
          </CardHeader>
          <CardContent>
            {orgs.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Aucun appel sur la période.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead className="text-right">Appels</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Coût estimé</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgs.map((o) => (
                    <TableRow key={o.organization_id ?? "platform"}>
                      <TableCell>
                        {o.organization_name}
                        {o.organization_id === null && (
                          <Badge variant="outline" className="ml-2">
                            interne
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{o.calls}</TableCell>
                      <TableCell className="text-right">
                        {formatTokens(o.prompt_tokens + o.completion_tokens)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatUsd(o.estimated_cost_usd)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Appels les plus coûteux</CardTitle>
            <p className="text-sm text-muted-foreground">
              Un pic vient presque toujours d'une poignée d'appels au prompt
              surdimensionné, pas d'une dérive uniforme — c'est la queue qu'il faut
              regarder, pas la moyenne.
            </p>
          </CardHeader>
          <CardContent>
            {topCalls.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Aucun appel tarifé sur la période.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Modèle</TableHead>
                    <TableHead className="text-right">Org.</TableHead>
                    <TableHead className="text-right">Entrée</TableHead>
                    <TableHead className="text-right">Sortie</TableHead>
                    <TableHead className="text-right">Durée</TableHead>
                    <TableHead className="text-right">Coût estimé</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topCalls.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {c.created_at
                          ? new Date(c.created_at).toLocaleString("fr-FR")
                          : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.operation}</TableCell>
                      <TableCell className="font-mono text-xs">{c.model}</TableCell>
                      <TableCell className="text-right text-xs">
                        {c.organization_id ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.prompt_tokens === null ? "—" : formatTokens(c.prompt_tokens)}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.completion_tokens === null
                          ? "—"
                          : formatTokens(c.completion_tokens)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {c.duration_seconds === null
                          ? "—"
                          : `${c.duration_seconds.toFixed(1)} s`}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatUsd(c.estimated_cost_usd)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        </main>
      </SidebarInset>
    </>
  );
}
